import { createContext, useContext, useState, type ReactNode } from 'react'

import { apiFetch } from '@/api/client'
import type {
  AuthenticatedLoginResponse,
  LoginResponse,
  UserSummary,
} from '@/api/types'
import { clearSession, getToken, getUser, saveSession } from '@/lib/token'

interface AuthState {
  token: string | null
  user: UserSummary | null
  login: (email: string, password: string) => Promise<LoginResponse>
  completeTotpSetup: (challengeToken: string, code: string) => Promise<void>
  verifyTotp: (challengeToken: string, code: string) => Promise<void>
  logout: () => void
}

const Ctx = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => getToken())
  const [user, setUser] = useState<UserSummary | null>(() => getUser())

  function applyAuthenticated(resp: AuthenticatedLoginResponse) {
    saveSession(resp.access_token, resp.user)
    setToken(resp.access_token)
    setUser(resp.user)
  }

  async function login(emailInput: string, password: string) {
    const resp = await apiFetch<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email: emailInput, password }),
    })
    if (resp.status === 'authenticated') {
      applyAuthenticated(resp)
    }
    return resp
  }

  async function completeTotpSetup(challengeToken: string, code: string) {
    const resp = await apiFetch<AuthenticatedLoginResponse>('/auth/login/totp/setup', {
      method: 'POST',
      body: JSON.stringify({ challenge_token: challengeToken, code }),
    })
    applyAuthenticated(resp)
  }

  async function verifyTotp(challengeToken: string, code: string) {
    const resp = await apiFetch<AuthenticatedLoginResponse>('/auth/login/totp/verify', {
      method: 'POST',
      body: JSON.stringify({ challenge_token: challengeToken, code }),
    })
    applyAuthenticated(resp)
  }

  function logout() {
    clearSession()
    setToken(null)
    setUser(null)
  }

  const value = { token, user, login, completeTotpSetup, verifyTotp, logout }
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAuth(): AuthState {
  const v = useContext(Ctx)
  if (!v) throw new Error('useAuth must be used inside AuthProvider')
  return v
}
